// src/features/trainings/TrainingProfile.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Badge, Spinner } from 'react-bootstrap';
import { FaEdit, FaArrowLeft, FaTrashAlt, FaUserFriends } from 'react-icons/fa';
import TrainingPDF from './TrainingPDF'; // Asegúrate de que la ruta sea correcta
import dayjs from 'dayjs';
import { useNavigationHistory } from '../../hooks/useNavigationHistory';
// Componentes
import TableWrapper from '../../components/TableWrapper';
import CustomSnackbar from '../../components/CustomSnackbar';

// Servicios y constantes
import TrainingsService from './TrainingsService';
import { TIPOS_CAPACITACIONES, getTipoDescripcion, FORMATO_FECHA } from '../../utils/constants';

/**
 * Componente para mostrar el detalle de una capacitación
 */
function TrainingProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { goBack } = useNavigationHistory();
  // Estados
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });

  // Cargar datos de la capacitación con useCallback
  const loadTrainingData = useCallback(async () => {
    try {
      setLoading(true);
      const trainingData = await TrainingsService.getTraining(id);
      setTraining(trainingData);
    } catch (error) {
      console.error('Error al cargar los datos de la capacitación:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al cargar los datos de la capacitación'
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Cargar datos al iniciar
  useEffect(() => {
    loadTrainingData();
  }, [loadTrainingData]);

  // Navegar a edición
  const handleEditTraining = () => {
    navigate(`/trainings/${id}/edit`);
  };




  // Eliminar capacitación
  const handleDeleteTraining = async () => {
    if (window.confirm(`¿Está seguro que desea eliminar esta capacitación?`)) {
      try {
        await TrainingsService.deleteTraining(id);
        
        setSnackbar({
          open: true,
          type: 'success',
          message: 'Capacitación eliminada correctamente'
        });
        
        // Redirigir a la lista después de un breve delay
        setTimeout(() => {
          navigate('/trainings');
        }, 1500);
      } catch (error) {
        console.error('Error al eliminar capacitación:', error);
        setSnackbar({
          open: true,
          type: 'error',
          message: 'Error al eliminar la capacitación'
        });
      }
    }
  };



  // Cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Columnas para la tabla de participantes
  const personColumns = [
    { 
      headerName: 'Nombre', 
      field: 'firstName', 
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Apellido', 
      field: 'surname', 
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Documento', 
      field: 'document', 
      width: 130,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Teléfono', 
      field: 'phone', 
      width: 130,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Email', 
      field: 'email', 
      flex: 1.5,
      filter: 'agTextColumnFilter',
      floatingFilter: true
    }
  ];

  // Mostrar spinner durante la carga
  if (loading && !training) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  // Si no se encuentra la capacitación
  if (!training) {
    return (
      <Container className="text-center mt-5">
        <h3>No se encontró la capacitación solicitada</h3>
        <Button variant="primary" onClick={goBack} className="mt-3">
          Volver a la lista
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="p-0">
      {/* Barra de acciones superior */}
      <div className="bg-light p-2 d-flex align-items-center">
        <Button 
          variant="outline-secondary" 
          onClick={() => goBack('/trainings')}
          className="me-2"
          size="sm"
        >
          <FaArrowLeft className="me-1" /> Volver
        </Button>
        <Button 
          variant="outline-primary" 
          onClick={handleEditTraining}
          className="me-2"
          size="sm"
        >
          <FaEdit className="me-1" /> Editar
        </Button>
        <Button 
          variant="outline-danger" 
          onClick={handleDeleteTraining}
          className="me-2"
          size="sm"
        >
          <FaTrashAlt className="me-1" /> Eliminar
        </Button>
          <TrainingPDF training={training}  /> {/* GENERAR PDF */}
        
      </div>

      <Row className="m-0">
        {/* Columna izquierda (Información general) */}
        <Col md={4} className="p-2">
          <Card className="mb-3">
            <Card.Header className="bg-light">
              <strong>Información General</strong>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col xs={4} className="text-muted">Tipo:</Col>
                <Col xs={8}><strong>{getTipoDescripcion(training.type, TIPOS_CAPACITACIONES)}</strong></Col>
              </Row>
              <Row className="mb-2">
                <Col xs={4} className="text-muted">Fecha:</Col>
                <Col xs={8}>{dayjs(training.date).format(FORMATO_FECHA)}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={4} className="text-muted">Instructor:</Col>
                <Col xs={8}>{training.instructor}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={4} className="text-muted">Tema:</Col>
                <Col xs={8}>{training.topic}</Col>
              </Row>
              <Row className="mb-2">
                <Col xs={4} className="text-muted">Participantes:</Col>
                <Col xs={8}>
                  <Badge bg="primary">
                    {training.persons?.length || 0} personas
                  </Badge>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Observaciones */}
          <Card>
            <Card.Header className="bg-light">
              <strong>Observaciones</strong>
            </Card.Header>
            <Card.Body>
              {training.observations 
                ? <p>{training.observations}</p>
                : <p className="text-muted">No hay observaciones registradas</p>
              }
            </Card.Body>
          </Card>
        </Col>

        {/* Columna derecha (Lista de participantes) */}
        <Col md={8} className="p-2">
          <Card>
            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
              <strong>
                <FaUserFriends className="me-2" />
                Participantes
              </strong>
              <Badge bg="primary" pill>
                {training.persons?.length || 0}
              </Badge>
            </Card.Header>
            <Card.Body>
              {training.persons && training.persons.length > 0 ? (
                <TableWrapper
                  columnDefs={personColumns}
                  rowData={training.persons}
                  tableType="personas"
                  customGridOptions={{
                    pagination: true,
                    paginationPageSize: 10,
                    domLayout: 'autoHeight'
                  }}
                />
              ) : (
                <div className="text-center p-4">
                  <p className="text-muted">No hay participantes asignados a esta capacitación</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <CustomSnackbar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={handleCloseSnackbar}
      />
    </Container>
  );
}

export default TrainingProfile;